/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FilterPanel } from '@gsa/afp-component-library';
import { getFilterStructure } from './util/standard-item-filter-structure';
import { useStandards } from './standards-provider';
import { standardItemOptions } from './util/standard-item-filter-helper';
import useLocalStorage from '../../hooks/use-local-storage';

const filterKeys = ['tags', 'status', 'vehicleType', 'vehicleGroup', 'fedStandardCode'];

const StandardItemFilters = ({
  currentFilters,
  setFilters,
}) => {
  const {
    multipleOptions,
    getStandardItemOptions,
    selectedOptions,
    setData,
    selectedYearFilter,
    getFilterTypeAheadOptions,
    typeaheadData,
  } = useStandards();

  // console.log('selectedOptions >>>>>>>>>>>', selectedOptions);

  const { FilterPanel: SIComponentFilterPanel } = FilterPanel;
  const [rerenderKey, setRerenderKey] = useState(1);

  useEffect(() => {
    getStandardItemOptions({
      variables: {
        options: standardItemOptions,
      },
    });
  }, []);

  const [standardItemsFilter, setStandardItemFilter] = useLocalStorage(
    'STANDARD_ITEM_FILTER',
    null,
  );

  const filterStructure = useMemo(
    () => getFilterStructure(multipleOptions, standardItemsFilter),
    [multipleOptions, standardItemsFilter],
  );

  useEffect(() => {
    if (standardItemsFilter) {
      setData('SET_SELECTED_VEH_HIERARCHY', {
        fedStandardCode: standardItemsFilter.fedStandardCode,
        vehicleGroup: standardItemsFilter.vehicleGroup,
        vehicleType: standardItemsFilter.vehicleType,
      });
    }
  }, []);

  const resetToDefaultFilters = () => {
    setStandardItemFilter('STANDARD_ITEM_FILTER', null);
    setFilters({
      operator: 'AND',
      value: [
        {
          key: 'year',
          operator: 'EQ',
          value: `${new Date().getFullYear() + 1}`,
        },
        {
          key: 'isActive',
          operator: 'IN',
          value: [1],
        }
      ],
    });
    setRerenderKey(rerenderKey + 1);
  };

  const getLabelByFilterNameAndValue = (filterName, filterValue) => {
    if (!multipleOptions) return '';
    const filterOptions = multipleOptions[filterName]?.find(
      ({ value }) => value === filterValue,
    );
    return filterOptions?.label;
  };

  const getValueFromLabel = (label) => {
    return label?.split('-')[0];
  };

  const handleFiltersChange = (updatedFilters) => {
    // Flatten hierarchical filters (federalStandardsHierarchy) into individual filters
    const flattenedFilters = [];
    updatedFilters?.forEach((filter) => {
      if (filter.filters && Array.isArray(filter.filters)) {
        // This is a grouped filter like federalStandardsHierarchy
        flattenedFilters.push(...filter.filters);
      } else {
        flattenedFilters.push(filter);
      }
    });

    const filterByKeys = flattenedFilters?.reduce(
      (filterObj, currentFilterObj) => {
        return { ...filterObj, [currentFilterObj?.key]: currentFilterObj };
      },
      {},
    );

    const newFilter = {
      ...currentFilters,
      value: [...flattenedFilters.filter((f) => !filterKeys.includes(f.key))],
    };

    if (filterByKeys?.year) {
      setData('SET_SELECTED_FILTER_YEAR', filterByKeys?.year.value);
    }

    if (filterByKeys?.status) {
      newFilter.value.push({
        key: 'isActive',
        operator: 'IN',
        value: filterByKeys.status?.value,
      });
    }

    if (filterByKeys?.tags) {
      newFilter.value.push({
        key: 'StandardItem.tags', // tags is ambiguous as it also exists in associated table.
        operator: 'JSON_CONTAINS',
        value: { value: filterByKeys.tags?.value },
      });
    }

    const vehicleTypeOrFedStdCodeOrVehGroupFilterIsSelected =
      filterByKeys?.vehicleType || filterByKeys?.fedStandardCode || filterByKeys?.vehicleGroup;

    if (vehicleTypeOrFedStdCodeOrVehGroupFilterIsSelected) {
      const vehicleTypeFilter = filterByKeys?.vehicleType;
      const fedStandardCodeFilter = filterByKeys?.fedStandardCode;
      const vehicleGroupFilter = filterByKeys?.vehicleGroup;

      const vehicleTypeFilterIsSelected =
        vehicleTypeFilter &&
        vehicleTypeFilter.value !== selectedOptions?.vehicleType;
      const fedStandardCodeFilterIsSelected =
        fedStandardCodeFilter &&
        fedStandardCodeFilter.value !== selectedOptions?.fedStandardCode;
      const vehicleGroupFilterIsSelected =
        vehicleGroupFilter &&
        vehicleGroupFilter.value !== selectedOptions?.vehicleGroup;

      if (vehicleTypeFilterIsSelected) {
        setData('SET_SELECTED_VEH_HIERARCHY', {
          fedStandardCode: selectedOptions?.fedStandardCode || '',
          vehicleGroup: selectedOptions?.vehicleGroup || '',
          vehicleType: vehicleTypeFilter.value,
        });
      } else if (vehicleGroupFilterIsSelected) {
        setData('SET_SELECTED_VEH_HIERARCHY', {
          fedStandardCode: selectedOptions?.fedStandardCode || '',
          vehicleGroup: vehicleGroupFilter.value,
          vehicleType: '',
        });
      } else if (fedStandardCodeFilterIsSelected) {
        setData('SET_SELECTED_VEH_HIERARCHY', {
          fedStandardCode: fedStandardCodeFilter.value,
          vehicleGroup: '',
          vehicleType: '',
        });
      }
    }

    if (filterByKeys?.vehicleType) {
      newFilter.value.push({
        key: '$vehicleTypeCode.code$',
        operator: 'EQ',
        value: filterByKeys?.vehicleType?.value
      });
    }

    if (filterByKeys?.vehicleGroup) {
      newFilter.value.push({
        key: '$vehicleTypeCode.parent_code$',
        operator: 'EQ',
        value: filterByKeys?.vehicleGroup?.value
      });
    }

    if (filterByKeys?.fedStandardCode) {
      newFilter.value.push({
        key: 'fedStandardCode',
        operator: 'EQ',
        value: filterByKeys?.fedStandardCode?.value
      });
    }

    let selectedStandardItemFilterValues = newFilter.value.reduce(
      (reducedStdFilter, currentFilter) => {
        if (currentFilter?.key === 'StandardItem.tags') {
          return { ...reducedStdFilter, tags: currentFilter.value.value };
        }
        if (
          currentFilter?.key === 'vehicleType' ||
          currentFilter?.key === 'vehicleGroup' ||
          currentFilter?.key === 'fedStandardCode'
        ) {
          return {
            ...reducedStdFilter,
            [currentFilter.key]: getLabelByFilterNameAndValue(
              currentFilter.key,
              currentFilter.value,
            ),
          };
        }
        return {
          ...reducedStdFilter,
          [currentFilter.key]: currentFilter.value,
        };
      },
      {},
    );

    if (filterByKeys?.status) {
      selectedStandardItemFilterValues = {
        ...selectedStandardItemFilterValues,
        status: filterByKeys?.status.value,
      };
    }

    setData('SET_TYPEAHEAD_FILTER_DATA', null);
    setStandardItemFilter(selectedStandardItemFilterValues);

    setFilters(newFilter);
  };

  const handleFetchTypeaheads = ({ variables: queryVariables }) => {
    const selectedKey = queryVariables?.field;
    const filterObj = queryVariables?.filters[0]?.conditions?.find(
      (filter) => filter.key === selectedKey,
    );

    getFilterTypeAheadOptions({
      variables: {
        key: selectedKey,
        search: filterObj.value,
        filters: [
          {
            operator: '$and',
            conditions: [
              { operator: '$exact', key: 'year', value: selectedYearFilter },
            ],
          },
        ],
      },
    });
  };
  return (
    <SIComponentFilterPanel
      key={rerenderKey}
      clearButtonLabel="Reset All"
      filterStructure={filterStructure}
      model="standardItem"
      order={[['standardItemNumber', 'ASC']]}
      setQueryFiltersState={handleFiltersChange}
      fetchTypeaheads={handleFetchTypeaheads}
      typeaheadData={typeaheadData}
      showClearIcon
      handleClearAll={resetToDefaultFilters}
      retainFilters
    />
  );
};

StandardItemFilters.propTypes = {
  currentFilters: PropTypes.arrayOf(
    PropTypes.shape({
      operator: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.array,
      ]),
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }),
  ).isRequired,
  setFilters: PropTypes.func.isRequired,
};

export default StandardItemFilters;
